import React, { Component } from 'react';
import { func, object, number, array } from 'prop-types';
import { translate } from 'react-i18next';
import { inject, observer } from 'mobx-react';
import axios from 'axios';
import { ModuleRegistry } from 'react-module-container';
import Widget from '../widget';
import styles from './styles.scss';

import {
  Row,
  Container,
  Page,
  Heading,
  Text,
  TextButton,
  Box,
  Loader,
} from 'wix-style-react';
import {
  isAppInstalled,
  appDefIds,
  getCurrentInstance,
} from '@wix/business-manager-api';
import { isPartner } from '../../utils/data';
import { CookieConsentBanner } from '../cookie-consent-banner';
import {
  isCookiesBannerDismissed,
  dismissCookiesBanner,
  removeCookiesBannerDismissed,
} from '../../services/user-preferences';

const WidgetsObjectsRow = ({ widgets, numberOfWidgets }) => {
  const span = numberOfWidgets && Math.floor(12 / numberOfWidgets);
  return (
    <Row>
      {widgets.map((widget, index) => {
        return (
          <Widget
            type={widget.name}
            widgetObject={widget}
            key={widget.name + index}
            span={span}
          />
        );
      })}
    </Row>
  );
};

WidgetsObjectsRow.propTypes = {
  widgets: array.isRequired,
  numberOfWidgets: number.isRequired,
};

class Dashboard extends Component {
  static propTypes = {
    appConfig: object,
    biLogger: object,
    embeddedIntegrations: object,
    view: object,
    t: func,
    experiments: object,
  };

  state = {
    widgets: [],
    isCookiesBannerShown: false,
  };

  async componentDidMount() {
    this.props.biLogger.integrationsHomePageView();
    this.setState({
      widgets: await this.getWidgets(),
      isCookiesBannerShown: await this.shouldSeeCookiesBanner(),
    });
  }

  getWidgets = async () => {
    const {
      appConfig: {
        userId,
        metaSiteId,
        experiments,
        getNonRedirectedWidgetItems,
      },
      embeddedIntegrations: { getIntegrationStatus },
    } = this.props;

    const isPartnerSite =
      experiments['specs.promote.mi.ui.showMailChimpToPartners'] === 'true' &&
      (await isPartner({ userId, metaSiteId }));
    const isMailChimpInstalled = isAppInstalled(
      '151e32e8-3b14-ab49-6472-11b7d0510427',
    );

    const nonRedirected = getNonRedirectedWidgetItems;
    const isGoogleAdsInstalled =
      getIntegrationStatus('googleAdWords') !== 'new';

    return nonRedirected.filter(
      (widget) =>
        !(
          {
            mailChimp: !!(isPartnerSite || isMailChimpInstalled),
            gtag: !isGoogleAdsInstalled,
            googleAdWords: !!isGoogleAdsInstalled,
          }[widget.name] === false
        ),
    );
  };

  navigateToAppMarket = () => {
    this.props.navigation.navigateToAppMarket();
  };

  navigateToCookiesSettings = () => {
    this.props.navigation.navigateToCookiesSettings();
  };

  isConsentPolicyBannerInstalled = async () => {
    const currentInstance = getCurrentInstance(appDefIds.consentPolicyBanner);
    if (!currentInstance) {
      return false;
    }
    try {
      const { data } = await axios.get(
        `/_api/app-settings-service/v1/settings/components/${appDefIds.consentPolicyBanner}`,
        {
          params: {
            host: 'BUSINESS_MANAGER',
          },
          headers: {
            Authorization: currentInstance,
          },
        },
      );
      return data.settings.appEnabled;
    } catch (e) {
      return false;
    }
  };

  shouldSeeCookiesBanner = async () => {
    const {
      appConfig: { experiments },
    } = this.props;
    if (experiments['specs.promote.mi.ui.showCookieConsentBanner'] !== 'true') {
      return false;
    }
    const isBannerInstalled = await this.isConsentPolicyBannerInstalled();
    const isBannerDismissed = await isCookiesBannerDismissed();
    if (!isBannerInstalled) {
      await removeCookiesBannerDismissed();
    }
    return isBannerInstalled && !isBannerDismissed;
  };

  handleCloseCookiesBanner = async () => {
    this.setState({ isCookiesBannerShown: false });
    await removeCookiesBannerDismissed();
    await dismissCookiesBanner();
  };

  render() {
    const {
      t,
      view: { maxWidth },
      appConfig: { experiments },
    } = this.props;
    const { widgets, isCookiesBannerShown } = this.state;
    const redirected = this.props.appConfig.getRedirectedWidgetItems;
    const AppsCollection = ModuleRegistry.component('AppsCollectionWidget');

    const numberOfWidgets = 3;
    return (
      <Page maxWidth={maxWidth} dataHook="dashboard-main">
        <Page.Header
          title={t('dashboard.title')}
          subtitle={t('dashboard.header.subtitle')}
          hasBackgroundImage
        />
        <Page.Content>
          {experiments['specs.promote.mi.ui.showCookieConsentBanner'] ===
            'true' &&
            isCookiesBannerShown && (
              <CookieConsentBanner
                onEditSettings={this.navigateToCookiesSettings}
                onClose={this.handleCloseCookiesBanner}
              />
            )}
          {widgets.length === 0 ? (
            <div className={styles.loaderContainer}>
              <div className={styles.loader}>
                <Loader data-hook="dashboard-page-loader" />
              </div>
            </div>
          ) : (
            <Box paddingBottom="50px">
              <Container>
                <div
                  data-hook="market-integration-inhouse"
                  className={styles.inHouseWidgetsSection}
                >
                {widgets.map((widget, index) => { // eslint-disable-line
                    if (index % numberOfWidgets === 0) {
                      const rowWidgets = widgets.slice(
                        index,
                        index + numberOfWidgets,
                      );
                      return (
                        <WidgetsObjectsRow
                          key={index}
                          widgets={rowWidgets}
                          numberOfWidgets={numberOfWidgets}
                        />
                      );
                    }
                  })}
                </div>
                <div data-hook="app-market-section">
                  <Heading
                    appearance="H2"
                    className={styles.appMarketHeaderMargin}
                  >
                    {' '}
                    {t(
                      'marketing-integration.dashboard.app-market.section-header',
                    )}
                  </Heading>
                  <div className={styles.subHeaderRow}>
                    <div>
                      <Text size="small" weight="thin">
                        {t(
                          'marketing-integration.dashboard.app-market.section-sub-header',
                        )}
                      </Text>
                    </div>
                    <div className={styles.colAlignRight}>
                      <TextButton
                        as="a"
                        dataHook={`promote-marketing-integration-goto-app-market`}
                        onClick={this.navigateToAppMarket}
                        underline="none"
                        size="small"
                        weight="normal"
                      >
                        {t('marketing-integration.dashboard.app-market.link')}
                      </TextButton>
                    </div>
                  </div>
                  <div className={styles.divider} />
                  {experiments[
                    'specs.promote.mi.ui.showExternalAppMarketWidgetSection'
                  ] === 'true' ? (
                    <AppsCollection
                      locale={this.props.i18n.language}
                      metaSiteId={this.props.appConfig.metaSiteId}
                      collectionTag="marketing-integrations"
                      originPageComponentId={
                        'MarketingIntegrationLazyComponent'
                      }
                      originPageComponentLabel={t(
                        'marketing-integration.dashboard.app-market.back-to-mi',
                      )}
                    />
                  ) : (
                    <div>
                  {redirected.map((widget, index) => { // eslint-disable-line
                        if (index % numberOfWidgets === 0) {
                          const rowWidgets = redirected.slice(
                            index,
                            index + numberOfWidgets,
                          );
                          return (
                            <WidgetsObjectsRow
                              key={index}
                              widgets={rowWidgets}
                              numberOfWidgets={numberOfWidgets}
                            />
                          );
                        }
                      })}
                    </div>
                  )}
                </div>
              </Container>
            </Box>
          )}
        </Page.Content>
      </Page>
    );
  }
}

export default translate()(
  inject(
    'appConfig',
    'biLogger',
    'view',
    'embeddedIntegrations',
    'navigation',
  )(observer(Dashboard)),
);
